import React, { useEffect, useMemo, useState } from 'react';
import { OvalLoadingSpinner } from '@/components/__UI';
import { GridWrapMobile, StyledPreviousButton } from './BookingCalendar.styles';
import dayjs from 'dayjs';
import Calendar from '@/components/Calendar';
import { useQueryBookings, useQueryClosures } from '@/hooks/useQuery'

import { CONSOLIDATED_BOOKING_STATE_CANCELLED } from '@/constants/bookings';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { InfiniteScrollTrigger } from '@/components/InfiniteScrollTrigger';

export function BookingCalendarMonth({
  year,
  yearCalendar,
  propertyId,
  unitId,
  setSelectedDay,
  setSelectedBookingID,
  hasAuth,
  setBookings,
  setClosures,
  contractExpiry,
  showWeekNumber,
}) {


  const { t } = useTranslation();
  const enabled = !yearCalendar && !!propertyId;
  const [hoveredID, setHoveredID] = useState(null);
  const [visibleMonths, setVisibleMonths] = useState([]);
  const [showingAllPrevious, setShowingAllPrevious] = useState(false);
  const [renderedMonthCount, setRenderedMonthCount] = useState(3);

  const { data, isLoading } = useQueryBookings(
    {
      propertyId,
      unitId,
      searchBy: 'arrival',
      startDate: `${year - 1}-01-01`,
      endDate: `${year + 2}-12-31`,
    },
    { enabled }
  );

  const startDate = `${year - 1}-12-01`
  const endDate = `${year+ 2}-12-31`


  const { data: closures, isLoading: isLoadingClosures } = useQueryClosures(
    { propertyId, unitId, startDate, endDate }, 
    { enabled: enabled }
  )



  const bookings = useMemo(() => {
    let list = data?.bookings ?? [];
    list = list.filter((b) => b.state !== CONSOLIDATED_BOOKING_STATE_CANCELLED);
    const uniqueBookings = new Map();
    for (const booking of list) {
      uniqueBookings.set(booking.bookingId, booking);
    }
    return Array.from(uniqueBookings.values());
  }, [data]);

  useEffect(() => {
    if (enabled) setBookings(bookings);
  }, [enabled, bookings, setBookings]);

  useEffect(() => {
    if (enabled)
       setClosures(closures);
  }, [enabled, closures, setClosures]);



  const fullMonthList = useMemo(() => {
    const start = dayjs()
      .startOf('month')
      .subtract(1, 'year');
    const end = dayjs()
      .startOf('month')
      .add(2, 'year');

    const months = [];
    let current = start;

    while (current.isBefore(end) || current.isSame(end, 'month')) {
      months.push(current.format('YYYY-MM-DD'));
      current = current.add(1, 'month');
    }

    return months;
  }, []);

  const currentMonthIndex = useMemo(() => {
    const today = dayjs();
    return fullMonthList.findIndex((m) => dayjs(m).isSame(today, 'month'));
  }, [fullMonthList]);

  useEffect(() => {
    if (
      fullMonthList.length &&
      visibleMonths.length === 0 &&
      currentMonthIndex >= 0
    ) {
      const futureMonths = fullMonthList.slice(
        currentMonthIndex,
        currentMonthIndex + renderedMonthCount
      );
      setVisibleMonths(futureMonths);
    }
  }, [fullMonthList, visibleMonths, currentMonthIndex, renderedMonthCount]);

  const loadMoreFuture = () => {
    const lastVisible = visibleMonths[visibleMonths.length - 1];
    const lastIndex = fullMonthList.findIndex((m) => m === lastVisible);
    const more = fullMonthList.slice(lastIndex + 1, lastIndex + 1 + 3);
    setVisibleMonths((prev) => [...prev, ...more]);
  };

  const showPreviousMonths = () => {
    const previousMonths = fullMonthList.slice(0, currentMonthIndex);
    setVisibleMonths((prev) => [...previousMonths, ...prev]);
    setShowingAllPrevious(true);
  };

  if (isLoading || isLoadingClosures) return <OvalLoadingSpinner data-testid="spinner" />;

  return (
    <>
      {!showingAllPrevious && currentMonthIndex > 0 && (
        <StyledPreviousButton
          iconPosition="left"
          onClick={showPreviousMonths}
          text={t('showPreviousMonths')}
          colourScheme="brand-primary"
          variant="text"
        />
      )}
      <GridWrapMobile style={{ marginLeft: '-1rem', marginRight: '-1rem' }}>
        {visibleMonths.map((date) => {
          const segments = bookings.filter((b) => {
            const startDate = dayjs(b.startDate);
            const endDate = dayjs(b.endDate);
            const firstDayOfMonth = dayjs(date).startOf('month');
            const lastDayOfMonth = dayjs(date).endOf('month');

            const overlapsCurrentMonth =
              startDate.isBefore(lastDayOfMonth, 'day') &&
              endDate.isAfter(firstDayOfMonth, 'day');
            const isSameMonth =
              startDate.isSame(date, 'month') || endDate.isSame(date, 'month');

            return isSameMonth || overlapsCurrentMonth;
          });

          return (
            <div key={date} data-testid={`month-${dayjs(date).format('MMMM')}`}>
              <Calendar
                date={dayjs(date)}
                onDayClick={setSelectedDay}
                onSegmentClick={({ id }) => setSelectedBookingID(id)}
                segments={segments.map((item) => ({
                  id: item.bookingId,
                  state: item.state,
                  startDate: item.startDate,
                  endDate: item.endDate,
                }))}
                unavailableDates={[...closures, contractExpiry]}
                disabled={!hasAuth('createBooking')}
                yearCalendar={yearCalendar}
                showWeekNumber={showWeekNumber}
                hoveredID={hoveredID}
                setHoveredID={setHoveredID}
              />
            </div>
          );
        })}

        {visibleMonths[visibleMonths.length - 1] !==
          fullMonthList[fullMonthList.length - 1] && (
          <InfiniteScrollTrigger
            onVisible={loadMoreFuture}
            style={{ width: '100%' }}
          />
        )}
      </GridWrapMobile>
    </>
  );
}

BookingCalendarMonth.propTypes = {
  year: PropTypes.number.isRequired,
  yearCalendar: PropTypes.bool,
  propertyId: PropTypes.string,
  unitId: PropTypes.string,
  setSelectedDay: PropTypes.func.isRequired,
  setSelectedBookingID: PropTypes.func.isRequired,
  hasAuth: PropTypes.func.isRequired,
  setBookings: PropTypes.func.isRequired,
  setClosures: PropTypes.func.isRequired,
  contractExpiry: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    startDateIso: PropTypes.string,
    endDateIso: PropTypes.string,
  }),
  showWeekNumber: PropTypes.bool,
};
